
import React, { useState, useMemo, useCallback } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
    Tooltip, Chip, Menu, MenuItem, IconButton
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';
import { fetchDispatchReservations } from '../../api/dispatchService';
import { isVacantStatus } from '../../constants/calendar';



/**
 * アサイン候補の表示優先度を下げる車両ステータス（2026-08-10 追加）。
 * 米軍・宮古島は通常の貸出には回しにくいため、候補には残しつつ**一覧の最後**に表示する。
 * id ではなく code で判定する（店舗ごとの採番差異に耐えるため）。
 */
const DEPRIORITIZED_ASSIGN_STATUS_CODES = ['us_military', 'miyako_lease'];

interface VehicleAssignModalProps {
    open: boolean;
    onClose: () => void;
    reservation: any;
    vehicleList?: any[];
    vehicles?: Record<string, any>;
    carTypesList?: any[];
    onAssign: (reservation: any, vehicleId: any) => Promise<void> | void;
    onUpdateVehicleStatus?: (vehicleId: any, statusId: any) => Promise<void> | void;
    vehicleStatusList?: any[];
    allReservations?: any[];
    /** スマホ（PWA）では全画面で開く（2026-09-25） */
    fullScreen?: boolean;
}

/**
 * 車両アサインモーダル
 */

const VehicleAssignModal: React.FC<VehicleAssignModalProps> = ({
    open,
    onClose,
    reservation,
    vehicleList = [],
    vehicles = {},
    carTypesList = [],
    onAssign,
    onUpdateVehicleStatus,
    vehicleStatusList,
    allReservations = [], // 重複チェック用の全予約リスト
    fullScreen = false,
}) => {

    const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; vehicleId: any; message: string }>({ open: false, vehicleId: null, message: '' });
    const [statusMenu, setStatusMenu] = useState<{ anchor: HTMLElement | null; vehicle: any }>({ anchor: null, vehicle: null });

    // 表示用の車両ステータスリスト（プロップがあれば優先、なければ空配列）
    const effectiveVehicleStatusList = useMemo(() => {
        return (vehicleStatusList && vehicleStatusList.length > 0) ? vehicleStatusList : [];
    }, [vehicleStatusList]);

    // 表示優先度を下げるステータスID（code → id をマスタから解決。2026-08-10）
    const deprioritizedStatusIds = useMemo(() => {
        const set = new Set<number>();
        effectiveVehicleStatusList.forEach((s: any) => {
            if (s?.id != null && DEPRIORITIZED_ASSIGN_STATUS_CODES.includes(s.code)) set.add(Number(s.id));
        });
        return set;
    }, [effectiveVehicleStatusList]);


    // 予約の車種コードを正規化
    const resCarType = useMemo(() => {
        if (!reservation) return '';
        return (reservation.car_type || reservation.re_car_type || reservation.req_car_type || '').toLowerCase().trim();
    }, [reservation]);

    // 重複チェックロジック
    const checkVehicleConflict = useCallback((vehicleId: any, startStr: string, endStr: string, excludeResId: any) => {
        if (!vehicleId || !startStr || !endStr) return false;
        const start = dayjs(startStr);
        const end = dayjs(endStr);

        // 全予約リスト（propsから取得、なければ空配列）から重複チェック
        // 注: reservationSyncServiceは現在の日付から限られた期間のみキャッシュするため、
        // 将来の予約をチェックする場合は親コンポーネントから全予約を渡す必要がある
        const reservationsToCheck = allReservations.length > 0 ? allReservations : [];

        return reservationsToCheck.some((row: any) => {


            if (String(row.id) === String(excludeResId)) return false;
            // vehicle_id または vehicleId (APIレスポンスによる) をチェック
            const rowVehicleId = row.vehicle_id ?? row.vehicleId;
            if (String(rowVehicleId) !== String(vehicleId)) return false;


            // 車両を専有しないステータスは除外。
            // 正: md/share_md/22_reservation_inventory_busy_logic.md（VACANT_STATUS_CODES 4種）。
            // 旧実装は full(6) も空き扱いにしていたが、仕様では full/offer は busy
            // （オファー中の車両を二重提案しないため）。2026-08-08 に isVacantStatus へ統一。
            if (isVacantStatus(row.status)) return false;


            const resStart = dayjs(row.start_date);
            const resEnd = dayjs(row.end_date);

            // 期間が重複するか
            const isOverlapping = start.isBefore(resEnd) && end.isAfter(resStart);
            return isOverlapping;

        });
    }, [allReservations]);


    // 車両リストのソートと加工
    const processedVehicles = useMemo(() => {
        if (!reservation) return [];

        // アーカイブ済み（out_of_use）・退役済(3) を候補から除外
        // （ただし現在アサインされている車両は、外せなくならないよう常に表示する）
        const currentVehicleId = reservation.vehicle_id ?? reservation.vehicleId;
        const filtered = vehicleList.filter((v: any) =>
            (v.out_of_use !== true && v.status !== 3) ||
            (currentVehicleId != null && String(v.id) === String(currentVehicleId))
        );
        // 米軍・宮古島は候補には残すが、表示の優先順位を最後にする（2026-08-10）
        const isDeprioritized = (v: any) => deprioritizedStatusIds.has(Number(v.status));
        const sorted = [...filtered].sort((a: any, b: any) => {
            // 1. 米軍・宮古島は常に末尾へ
            const aLow = isDeprioritized(a) ? 1 : 0;
            const bLow = isDeprioritized(b) ? 1 : 0;
            if (aLow !== bLow) return aLow - bLow;

            const aTypeCode = (a.car_type_code || a.vehicle_type?.vehicle_type_code || '').toLowerCase().trim();
            const bTypeCode = (b.car_type_code || b.vehicle_type?.vehicle_type_code || '').toLowerCase().trim();

            // 2. 予約の車種に一致するものを優先
            const aMatch = resCarType && aTypeCode && resCarType === aTypeCode;
            const bMatch = resCarType && bTypeCode && resCarType === bTypeCode;

            if (aMatch && !bMatch) return -1;
            if (!aMatch && bMatch) return 1;
            return aTypeCode.localeCompare(bTypeCode);
        });

        return sorted.map((v: any) => {
            const vTypeCode = (v.car_type_code || v.vehicle_type?.vehicle_type_code || '').toLowerCase().trim();
            const vTypeName = v.vehicle_type?.name || v.vehicle_type_name || v.car_type || '';
            const typeMatch = !resCarType || (vTypeCode && resCarType === vTypeCode) || (vTypeName && resCarType === vTypeName.toLowerCase());
            const hasConflict = checkVehicleConflict(v.id, reservation.start_date, reservation.end_date, reservation.id);



            const currentVehicleId = reservation.vehicle_id ?? reservation.vehicleId;
            const isCurrentlyAssigned = currentVehicleId != null && String(v.id) === String(currentVehicleId);

            return {
                ...v,
                vTypeCode,
                vTypeName,
                typeMatch,
                hasConflict,
                isCurrentlyAssigned
            };
        });
    }, [vehicleList, reservation, resCarType, checkVehicleConflict, deprioritizedStatusIds]);

    // アサイン実行前確認
    const handleAssignClick = (vehicle: any) => {
        if (!reservation || !vehicle) return;

        // 両方OKならそのまま実行
        if (vehicle.typeMatch && !vehicle.hasConflict) {
            onAssign(reservation, vehicle.id);
            return;
        }

        // 確認ダイアログのメッセージ構築
        let messages: string[] = [];
        if (!vehicle.typeMatch) {
            messages.push(`車両タイプ「${vehicle.vTypeName}(${vehicle.vTypeCode})」は予約の車種「${resCarType}」と一致しません。`);
        }
        if (vehicle.hasConflict) {
            messages.push('この車両は利用期間中に他の予約が入っています（重複）。');
        }
        messages.push('アサインしますか？');

        setConfirmDialog({
            open: true,
            vehicleId: vehicle.id,
            message: messages.join('\n')
        });
    };

    const handleConfirmAssign = () => {
        if (confirmDialog.vehicleId) {
            onAssign(reservation, confirmDialog.vehicleId);
        }
        setConfirmDialog({ open: false, vehicleId: null, message: '' });
    };

    // ステータスメニュー関連
    const handleOpenStatusMenu = (event: any, vehicle: any) => {
        setStatusMenu({ anchor: event.currentTarget, vehicle });
    };

    const handleCloseStatusMenu = () => {
        setStatusMenu({ anchor: null, vehicle: null });
    };

    const handleStatusSelect = (statusId: any) => {
        if (statusMenu.vehicle && onUpdateVehicleStatus) {
            onUpdateVehicleStatus(statusMenu.vehicle.id, statusId);
        }
        handleCloseStatusMenu();
    };

    const formatDateRange = (start: any, end: any) => {
        if (!start || !end) return '-';
        return `${dayjs(start).format('MM/DD HH:mm')} 〜 ${dayjs(end).format('MM/DD HH:mm')}`;
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                fullScreen={fullScreen}
            >
                <DialogTitle>
                    車両アサイン
                    {reservation && (
                        <Typography variant="body2" color="text.secondary">
                            予約: {reservation.customer_name} | 期間: {formatDateRange(reservation.start_date, reservation.end_date)} | 希望車種: {
                                (() => {
                                    const found = carTypesList.find((c: any) => (c.code || '').toLowerCase() === resCarType || (c.name || '').toLowerCase() === resCarType);
                                    return found ? `${found.name} (${resCarType})` : resCarType;
                                })()
                            }
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>車両コード</TableCell>
                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>車両タイプ</TableCell>
                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc', width: 80, textAlign: 'center' }}>重複</TableCell>
                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc', width: 100 }}>操作</TableCell>
                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc', width: 100 }}>ステータス</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {processedVehicles.map((vehicle: any) => (
                                    <TableRow
                                        key={vehicle.id}
                                        hover
                                        sx={{
                                            backgroundColor: vehicle.isCurrentlyAssigned ? '#f0fdf4' : 'inherit',
                                            '&:hover': { backgroundColor: vehicle.isCurrentlyAssigned ? '#dcfce7 !important' : 'inherit' }
                                        }}
                                    >
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                component="div"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    color: vehicle.isCurrentlyAssigned ? 'success.main' : (vehicle.typeMatch ? '#1e293b' : 'text.disabled'),
                                                    fontWeight: vehicle.isCurrentlyAssigned ? 600 : 400,
                                                }}
                                            >
                                                {vehicle.vehicle_code || '-'}
                                                {vehicle.isCurrentlyAssigned && (
                                                    <Chip label="アサイン済" size="small" color="success" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: vehicle.typeMatch ? '#1e293b' : 'text.disabled' }}
                                            >
                                                {vehicle.vTypeName || '-'}
                                            </Typography>
                                            {vehicle.vTypeCode && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: vehicle.typeMatch ? 'text.secondary' : 'text.disabled',
                                                        display: 'block',
                                                        fontSize: '0.7rem'
                                                    }}
                                                >
                                                    {vehicle.vTypeCode}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {vehicle.hasConflict ? (
                                                <Tooltip title="利用期間中に他の予約があります">
                                                    <WarningAmberIcon color="warning" fontSize="small" />
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="body2" color="success.main" sx={{ opacity: 0.5 }}>−</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                size="small"
                                                variant={vehicle.isCurrentlyAssigned ? "text" : "outlined"}
                                                disabled={vehicle.isCurrentlyAssigned}
                                                onClick={() => handleAssignClick(vehicle)}
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {vehicle.isCurrentlyAssigned ? 'アサイン中' : 'アサイン'}
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={(e: any) => handleOpenStatusMenu(e, vehicle)}
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    padding: '2px 8px',
                                                    minWidth: 'auto',
                                                    boxShadow: 'none',
                                                    backgroundColor: effectiveVehicleStatusList.find((s: any) => s.id === vehicle.status)?.bg_color || '#e2e8f0',
                                                    color: effectiveVehicleStatusList.find((s: any) => s.id === vehicle.status)?.text_color || '#475569',
                                                    '&:hover': {
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                                        opacity: 0.9,
                                                        backgroundColor: effectiveVehicleStatusList.find((s: any) => s.id === vehicle.status)?.bg_color || '#e2e8f0'
                                                    }
                                                }}
                                            >
                                                {vehicle.status_name || effectiveVehicleStatusList.find((s: any) => s.id === vehicle.status)?.label || '未設定'}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {processedVehicles.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">表示できる車両がありません</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>閉じる</Button>
                </DialogActions>
            </Dialog>

            {/* アサイン確認ダイアログ */}
            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
            >
                <DialogTitle>アサインの確認</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
                        {confirmDialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>いいえ</Button>
                    <Button onClick={handleConfirmAssign} variant="contained" color="primary" autoFocus>
                        はい
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 車両ステータスメニュー */}
            <Menu
                anchorEl={statusMenu.anchor}
                open={Boolean(statusMenu.anchor)}
                onClose={handleCloseStatusMenu}
            >
                {effectiveVehicleStatusList.map((status: any) => (
                    <MenuItem
                        key={status.id}
                        onClick={() => handleStatusSelect(status.id)}
                        selected={statusMenu.vehicle?.status === status.id}
                    >
                        <Chip
                            label={status.label}
                            size="small"
                            sx={{
                                minWidth: 80,
                                cursor: 'pointer',
                                backgroundColor: status.bg_color || '#e2e8f0',
                                color: status.text_color || '#475569',
                                '&:hover': { opacity: 0.8 }
                            }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default VehicleAssignModal;
